import { useOrganizationContext } from '@/contexts/organization-context';
import { InputGroup } from '../../input-group';
import { TextareaGroup } from '../../textarea-group';
import SettingsTabTitle from '../settings-tab-title';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { gql } from '@/__generated__';
import { OrganizationGetByIdSuccess } from '@/__generated__/graphql';
import { logError } from '@/lib/rum';
import { OrganizationDelete } from './organization-delete';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Button } from '@/components/shadcn/button';
import { SparklerIconComponent } from '@/assets/IconComponents/SparklerIcon';
import { RoutePaths } from '@/consts/routes';
import { useRouter } from 'next/router';

gql(`fragment OrganizationTabResultFragment on Organization {
    orgData {
      ... on NpoData {
        mission 
      }
    }
    history
    id
    name
    website
} `);

const OrganizationGetByIdQuery = gql(`
  query OrganizationGetByIdQuery($input: OrganizationGetByIdInput!) {
    organization {
    getById(input: $input) {
      ... on OrganizationGetByIdSuccess {
        result {
          ...OrganizationTabResultFragment
        }
      }
    }
  }
  }
`);

export const UpdateOrganizationData =
  gql(`mutation OrganizationTabUpdateSettings($input: OrganizationUpdateInput!) {
    organization {
      update(input: $input) {
        ... on OrganizationUpdateSuccess {
          result {
          ...OrganizationTabResultFragment
          }
        }
      }
    }
  }
`);

export function OrganizationTab() {
  const isOnboardingAgentEnabled = useFeatureFlagEnabled(
    'grant-onboarding-agent'
  );
  const router = useRouter();
  const { organizationId } = useOrganizationContext();
  const [loadOrganizationData] = useLazyQuery(OrganizationGetByIdQuery);
  const [updateOrganization] = useMutation(UpdateOrganizationData);
  const [name, setName] = useState<string>();
  const [website, setWebsite] = useState<string>();
  const [mission, setMission] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [history, setHistory] = useState<string>();

  useEffect(() => {
    if (organizationId) {
      loadOrganizationData({
        variables: { input: { id: organizationId } },
      })
        .then((data) => {
          const orgData = (
            data?.data?.organization.getById as OrganizationGetByIdSuccess
          ).result;
          setName(orgData.name);
          setWebsite(orgData.website as string);
          setMission(orgData.orgData.mission as string);
          setHistory(orgData.history || '');
          setLoading(false);
        })
        .catch((e) =>
          logError(
            `Failed to get Organization data for organizationId ${organizationId}`,
            e
          )
        );
    }
  }, [organizationId, loadOrganizationData]);

  const saveOnInputBlur = () => {
    setLoading(true);
    updateOrganization({
      variables: {
        input: {
          set: {
            website: website,
            name: name,
            history: history,
            orgData: JSON.stringify({ mission }),
          },
          filters: {
            id: organizationId as string,
          },
        },
      },
    }).finally(() => setLoading(false));
  };

  if (!organizationId) {
    return (
      <div>
        <SettingsTabTitle title='Organization' />
        <p>Please select an organization first.</p>
      </div>
    );
  }

  return (
    <div>
      <SettingsTabTitle title='Organization' />

      <div className='flex flex-col gap-4'>
        <InputGroup
          labelTitle='Organization name'
          placeholder='Organization Name'
          disabled={loading}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={saveOnInputBlur}
        />

        <InputGroup
          labelTitle='Website'
          placeholder='Website'
          disabled={loading}
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          onBlur={saveOnInputBlur}
        />

        <TextareaGroup
          labelTitle='Mission'
          placeholder='Mission'
          disabled={loading}
          value={mission}
          onChange={(event) => setMission(event.target.value)}
          onBlur={saveOnInputBlur}
        />

        <TextareaGroup
          labelTitle='History'
          placeholder='History'
          disabled={loading}
          value={history}
          onChange={(event) => setHistory(event.target.value)}
          onBlur={saveOnInputBlur}
        />
      </div>

      <SettingsTabTitle title='Organization actions' className='mt-6' />
      <div className='flex justify-between'>
        <OrganizationDelete name={name} />
        {isOnboardingAgentEnabled && (
          <Button
            variant='maggie-secondary'
            icon={SparklerIconComponent}
            onClick={() => {
              router.push(
                `${RoutePaths.OnboardingAgent}?organizationId=${organizationId}`
              );
            }}
          >
            Onboard Organization
          </Button>
        )}
      </div>
    </div>
  );
}
